import { Fragment } from "react";
import { Button, Flex, IconButton, Tag, TagLabel, Tooltip } from "@chakra-ui/react";
import { AddIcon, CloseIcon } from "@chakra-ui/icons";
import {
  CatalogoDisparo,
  CombinadorFiltro,
  ContextoDisparo,
  MAX_CONDICOES_FILTRO,
  MAX_PROFUNDIDADE_FILTRO,
} from "@/models/consignado/disparo.model";
import { FiltroDraft } from "./disparo-draft";
import {
  COMBINADOR_LABEL,
  GrupoNo,
  podeAdicionarCondicao,
  podeAninhar,
} from "./filtro-arvore";
import FiltroCondicaoCard from "./FiltroCondicaoCard";
import { chaveNovaCondicao, chaveNovoSubgrupo, useFiltroActive } from "./FiltroActiveContext";
import { brandSolid, fieldFont } from "./styleTokens";

type Props = {
  grupo: GrupoNo;
  /** A raiz, para checar os tetos do backend a partir de qualquer nível. */
  arvore: GrupoNo;
  contexto: ContextoDisparo;
  catalogo?: CatalogoDisparo;
  /** Erros por id de condição — os mesmos do `porFiltro` do draft. */
  erros?: Record<string, string>;
  onAddCondicao: (grupoId: string) => void;
  onAddSubgrupo: (grupoId: string) => void;
  onUpdateCondicao: (id: string, patch: Partial<FiltroDraft>) => void;
  onUpdateCombinador: (grupoId: string, combinador: CombinadorFiltro) => void;
  onRemoveNo: (id: string) => void;
  /** undefined na raiz — a raiz não é removível, só esvaziada. */
  onRemoveSelf?: () => void;
};

// Conector E/OU entre duas condições adjacentes — não é um controle solto
// acima da fileira: fica literalmente entre as pills que combina, no
// espírito de "A E B". Clicar alterna (são só duas opções, não precisa de
// Select). Um grupo tem UM combinador, válido para todos os pares dele, então
// clicar em qualquer conector muda o grupo inteiro; para misturar E com OU é
// que existe o subgrupo.
function CombinadorConector({
  combinador,
  onChange,
}: {
  combinador: CombinadorFiltro;
  onChange: (combinador: CombinadorFiltro) => void;
}) {
  const outro =
    combinador === CombinadorFiltro.E ? CombinadorFiltro.OU : CombinadorFiltro.E;
  return (
    <Tooltip
      label={`${COMBINADOR_LABEL[combinador]} — clique para trocar para ${outro}`}
      placement="top"
      openDelay={300}
    >
      <Tag
        onClick={() => onChange(outro)}
        size="sm"
        borderRadius="md"
        cursor="pointer"
        flexShrink={0}
        letterSpacing="0.05em"
        {...brandSolid("secondary")}
        {...fieldFont}
      >
        <TagLabel fontWeight="700">{combinador}</TagLabel>
      </Tag>
    </Tooltip>
  );
}

/**
 * Renderiza a árvore recursivamente: cada nó é uma condição (a pill do
 * FiltroCondicaoCard) ou um subgrupo, que vira uma fileira própria com borda
 * tracejada — o parêntese visível.
 *
 * É o FiltroGrupoView da branch feat/storybook, portado para o catálogo
 * atual. Sem parêntese, "A E B OU C" é ambíguo — daí o subgrupo ser a única
 * forma de misturar E com OU.
 */
export default function FiltroGrupoView({
  grupo,
  arvore,
  contexto,
  catalogo,
  erros,
  onAddCondicao,
  onAddSubgrupo,
  onUpdateCondicao,
  onUpdateCombinador,
  onRemoveNo,
  onRemoveSelf,
}: Props) {
  const raiz = onRemoveSelf === undefined;

  // Tetos do backend (disparo-filtro.helper.ts). Bloquear aqui evita montar
  // uma árvore que o POST recusaria — e o tooltip diz o porquê, em vez de o
  // botão simplesmente não responder.
  const aninharLiberado = podeAninhar(arvore, grupo.id);
  const condicaoLiberada = podeAdicionarCondicao(arvore);

  // Clicado e esperando o campo na sidebar: o botão fica preenchido, já que a
  // condição só aparece depois da escolha e, sem isso, nada no formulário
  // indicaria que o clique foi registrado.
  const { activeKey } = useFiltroActive();
  const condicaoPendente = activeKey === chaveNovaCondicao(grupo.id);
  const subgrupoPendente = activeKey === chaveNovoSubgrupo(grupo.id);
  const destaque = { ...brandSolid("secondary"), _hover: { opacity: 0.9 } };

  return (
    <Flex
      direction="column"
      gap={1}
      {...(raiz
        ? {}
        : {
            borderWidth: "1px",
            borderColor: "secondary",
            borderStyle: "dashed",
            borderRadius: "md",
            p: 1.5,
          })}
    >
      {!raiz && (
        <Flex justify="flex-end">
          <IconButton
            aria-label="Remover grupo"
            icon={<CloseIcon boxSize={2} />}
            size="xs"
            variant="ghost"
            colorScheme="red"
            onClick={onRemoveSelf}
          />
        </Flex>
      )}

      <Flex align="center" gap={2} bg="gray.50" borderRadius="md" p={2} wrap="wrap">
        {grupo.itens.map((no, index) => (
          <Fragment key={no.id}>
            {index > 0 && (
              <CombinadorConector
                combinador={grupo.combinador}
                onChange={(c) => onUpdateCombinador(grupo.id, c)}
              />
            )}
            {no.tipoNo === "condicao" ? (
              <FiltroCondicaoCard
                filtro={no}
                contexto={contexto}
                catalogo={catalogo}
                erro={erros?.[no.id]}
                onUpdate={onUpdateCondicao}
                onRemove={onRemoveNo}
              />
            ) : (
              <FiltroGrupoView
                grupo={no}
                arvore={arvore}
                contexto={contexto}
                catalogo={catalogo}
                erros={erros}
                onAddCondicao={onAddCondicao}
                onAddSubgrupo={onAddSubgrupo}
                onUpdateCondicao={onUpdateCondicao}
                onUpdateCombinador={onUpdateCombinador}
                onRemoveNo={onRemoveNo}
                onRemoveSelf={() => onRemoveNo(no.id)}
              />
            )}
          </Fragment>
        ))}

        <Tooltip
          label={
            condicaoPendente
              ? "Escolha o campo na barra lateral (clique de novo para cancelar)"
              : condicaoLiberada
              ? "Adicionar condição — o campo é escolhido na barra lateral"
              : `Teto de ${MAX_CONDICOES_FILTRO} condições na árvore inteira.`
          }
          openDelay={300}
        >
          <span>
            <IconButton
              aria-label="Adicionar condição"
              icon={<AddIcon boxSize={3} />}
              size="sm"
              borderRadius="full"
              colorScheme="secondary"
              variant="outline"
              isDisabled={!condicaoLiberada}
              {...(condicaoPendente ? destaque : {})}
              onClick={() => onAddCondicao(grupo.id)}
            />
          </span>
        </Tooltip>
        <Tooltip
          label={
            subgrupoPendente
              ? "Escolha na barra lateral o campo da primeira condição do grupo (clique de novo para cancelar)"
              : aninharLiberado
              ? "Adicionar grupo — o parêntese, onde a regra de combinação é outra"
              : `O backend aceita profundidade ${MAX_PROFUNDIDADE_FILTRO}, e a condição conta como nível: cabe um único aninhamento.`
          }
          openDelay={300}
        >
          <span>
            <Button
              size="sm"
              borderRadius="full"
              variant="outline"
              colorScheme="secondary"
              fontWeight="700"
              isDisabled={!aninharLiberado}
              {...(subgrupoPendente ? destaque : {})}
              {...fieldFont}
              onClick={() => onAddSubgrupo(grupo.id)}
            >
              ( )
            </Button>
          </span>
        </Tooltip>
      </Flex>
    </Flex>
  );
}
